import {
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  SourceEntityRecordStatus,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  MedicareNumber,
  Patient,
  ReferralSource,
  getNationalNumber,
} from '@principle-theorem/principle-core';
import {
  Gender,
  IContact,
  IDVACard,
  IHealthFundCard,
  IReferralSource,
  IReferralSourceConfiguration,
  PatientStatus,
  type IBrand,
  type IContactNumber,
  type IFeeSchedule,
  type IMedicareCard,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  ISO_DATE_FORMAT,
  ISO_DATE_TIME_FORMAT,
  WithPrimaryKey,
  getError,
  Region,
  type INamedDocument,
  snapshotCombineLatest,
  toISODate,
  toNamedDocument,
  toTimestamp,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import * as he from 'he';
import { compact, first, orderBy, sortBy } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  BasePatientDestinationEntity,
  IPatientMigrationData,
  PATIENT_RESOURCE_TYPE,
} from '../../../destination/entities/patient';
import { MedicareNumberCandidate } from '../../../destination/entities/lib/medicare-number-candidate';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import { ContactSourceEntity } from '../../source/entities/contacts';
import {
  CorePracticePatientInsuranceDetail,
  PatientInsuranceDetailSourceEntity,
} from '../../source/entities/patient-insurance-details';
import {
  CorePracticePatient,
  PatientSourceEntity,
  type ICorePracticePatient,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { CorePracticeContactMappingHandler } from '../mappings/contact-to-referral-source';
import { CorePracticeInsuranceToFeeScheduleMappingHandler } from '../mappings/insurance-to-fee-schedule';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import { ExistingPatientDestinationEntity } from './existing-patients';

export const PATIENT_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_RESOURCE_TYPE,
    label: 'Patients',
    description: '',
  },
});

const DVA_INSURANCE_NAME_PATTERN = /veterans/i;
const NO_EXPIRY_YEAR = 3000;

export interface IPatientJobData {
  sourcePatient: IGetRecordResponse<CorePracticePatient>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  brand: WithRef<IBrand>;
  referralSources: WithPrimaryKey<
    ITranslationMap<IReferralSourceConfiguration>
  >[];
  contacts: WithPrimaryKey<ITranslationMap<IContact>>[];
  insuranceFeeSchedules: WithPrimaryKey<ITranslationMap<IFeeSchedule>>[];
  region: Region;
}

export class PatientDestinationEntity extends BasePatientDestinationEntity<CorePracticePatient> {
  destinationEntity = PATIENT_DESTINATION_ENTITY;
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    contacts: new ContactSourceEntity(),
    patientInsuranceDetails: new PatientInsuranceDetailSourceEntity(),
  };

  override destinationEntities = {
    existingPatients: new ExistingPatientDestinationEntity(),
  };

  customMappings = {
    staff: new CorePracticeStafferMappingHandler(),
    referralSources: new CorePracticeContactMappingHandler(),
    insuranceFeeSchedules:
      new CorePracticeInsuranceToFeeScheduleMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const brand$ = PracticeMigration.brand$(migration);
    const referralSources$ =
      this.customMappings.referralSources.getRecords(translationMap);
    const contacts$ = translationMap.getByType<IContact>(
      this.sourceEntities.contacts.sourceEntity.metadata.idPrefix
    );
    const insuranceFeeSchedules$ =
      this.customMappings.insuranceFeeSchedules.getRecords(translationMap);
    const region$ = PracticeMigration.organisation$(migration).pipe(
      map((organisation) => organisation.region)
    );

    return snapshotCombineLatest([
      staff$,
      brand$,
      referralSources$,
      contacts$,
      insuranceFeeSchedules$,
      region$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          patientRecords,
          [
            staff,
            brand,
            referralSources,
            contacts,
            insuranceFeeSchedules,
            region,
          ],
        ]) =>
          patientRecords.map((sourcePatient) => ({
            sourcePatient,
            staff,
            brand,
            referralSources,
            contacts,
            insuranceFeeSchedules,
            region,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IPatientJobData
  ): Promise<
    | IPatientMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      if (
        data.sourcePatient.record.status === SourceEntityRecordStatus.Invalid
      ) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          'Source patient is invalid'
        );
      }

      const patient = data.sourcePatient.data;
      const sourcePatientId =
        this.sourceEntities.patients.getSourceRecordId(patient);

      const insuranceDetails =
        await this.sourceEntities.patientInsuranceDetails.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const referrer = await this.getReferralSource(
        patient,
        data.referralSources,
        data.contacts
      );

      return {
        sourcePatientId: sourcePatientId.toString(),
        createdAt: data.sourcePatient.record.translations.joinDate,
        patient: Patient.init({
          name: getName(patient),
          email: patient.email,
          contactNumbers: getPhone(patient, data.region),
          address: getAddress(patient),
          dateOfBirth: patient.dateOfBirth
            ? toISODate(moment.utc(patient.dateOfBirth, ISO_DATE_FORMAT))
            : undefined,
          gender: getGender(patient),
          status: getStatus(patient),
          medicareCard: getMedicareCard(patient, insuranceDetails),
          referenceId: sourcePatientId.toString(),
          healthFundCard: getHealthcareCard(insuranceDetails),
          dvaCard: getDVACard(insuranceDetails),
          preferredFeeSchedule: await getPreferredFeeSchedule(
            insuranceDetails,
            data.insuranceFeeSchedules
          ),
          preferredPractice:
            migration.configuration.practices.length === 1
              ? first(migration.configuration.practices)
              : undefined,
          referrer,
        }),
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  async getReferralSource(
    patient: ICorePracticePatient,
    referralSources: WithPrimaryKey<
      ITranslationMap<IReferralSourceConfiguration>
    >[],
    contacts: WithPrimaryKey<ITranslationMap<IContact>>[]
  ): Promise<IReferralSource | undefined> {
    if (!patient.referralSourceId) {
      return;
    }

    const referralSourceMap = referralSources.find(
      (referralSource) =>
        referralSource.sourceIdentifier === patient.referralSourceId?.toString()
    );
    if (referralSourceMap?.destinationIdentifier) {
      const referralSource = await Firestore.getDoc(
        referralSourceMap.destinationIdentifier
      );
      return ReferralSource.toReferrer(referralSource);
    }

    const matchingContact = contacts.find(
      (contact) =>
        contact.sourceIdentifier === patient.referralSourceId?.toString()
    );
    if (!matchingContact?.destinationIdentifier) {
      return;
    }

    return ReferralSource.toReferrer(
      await Firestore.getDoc(matchingContact.destinationIdentifier)
    );
  }
}

export function getGender(patient: ICorePracticePatient): Gender {
  switch (patient.sex) {
    case 'F':
      return Gender.Female;
    case 'M':
      return Gender.Male;
    case 'O':
      return Gender.Other;
    default:
      return Gender.NotSpecified;
  }
}

function getStatus(patient: ICorePracticePatient): PatientStatus {
  if (!patient.isActive || patient.isDeleted) {
    return PatientStatus.Inactive;
  }
  return PatientStatus.Active;
}

function getAddress(patient: ICorePracticePatient): string {
  const addressFields = compact([
    patient.addressLine1?.trim(),
    patient.addressLine2?.trim(),
    patient.suburb?.trim(),
    patient.state?.trim(),
    patient.country?.trim(),
    patient.postcode?.toString().trim(),
  ]);

  if (!addressFields.length) {
    return '';
  }

  return addressFields.join(', ');
}

export function getPhone(
  patient: ICorePracticePatient,
  region: Region
): IContactNumber[] {
  const contactNumbers: IContactNumber[] = [
    { label: 'mobile', number: restoreLeadingZero(patient.mobile, region) },
    { label: 'home', number: restoreLeadingZero(patient.homePhone, region) },
    { label: 'work', number: restoreLeadingZero(patient.workPhone, region) },
  ];
  return contactNumbers.filter((contactNumber) => !!contactNumber.number);
}

/**
 * Core Practice stores phone numbers as integers, which strips the leading
 * trunk zero (e.g. 0412 345 678 becomes 412345678).
 */
export function restoreLeadingZero(
  phoneNumber: string | undefined,
  region: Region
): string {
  const trimmed = phoneNumber?.trim() ?? '';
  return getNationalNumber(trimmed, region) ?? trimmed;
}

export function getName(patient: ICorePracticePatient): string {
  const preferredName = patient.preferredName?.trim() ?? '';

  if (!preferredName || preferredName === patient.firstName) {
    return he.decode(`${patient.firstName} ${patient.lastName}`.trim());
  }
  return he.decode(
    `${patient.firstName} (${preferredName}) ${patient.lastName}`.trim()
  );
}

export function getMedicareCard(
  patient: Pick<ICorePracticePatient, 'medicareNo'>,
  insuranceDetails: IGetRecordResponse<CorePracticePatientInsuranceDetail>[]
): IMedicareCard | undefined {
  const medicareNumber = MedicareNumberCandidate.choose([
    { value: patient.medicareNo },
    ...orderBy(
      insuranceDetails,
      (insuranceDetail) => insuranceDetail.data.id,
      'desc'
    ).map((insuranceDetail) => ({
      value: insuranceDetail.data.memberCode,
      irn: insuranceDetail.data.series,
      expiryDate: getExpiryDate(insuranceDetail.data.endDate),
    })),
  ]);

  if (medicareNumber) {
    return {
      number: medicareNumber.number,
      subNumerate: medicareNumber.irn ?? '',
      expiryDate: medicareNumber.expiryDate,
    };
  }

  if (!patient.medicareNo) {
    return;
  }

  return {
    number: patient.medicareNo,
    subNumerate: '',
  };
}

function isDVAInsurance(
  insuranceDetail: IGetRecordResponse<CorePracticePatientInsuranceDetail>
): boolean {
  return DVA_INSURANCE_NAME_PATTERN.test(insuranceDetail.data.insuranceName);
}

function getLatestWithMemberCode(
  cards: IGetRecordResponse<CorePracticePatientInsuranceDetail>[]
): IGetRecordResponse<CorePracticePatientInsuranceDetail> | undefined {
  return first(
    orderBy(
      cards.filter((insuranceDetail) => !!insuranceDetail.data.memberCode),
      (insuranceDetail) => insuranceDetail.data.id,
      'desc'
    )
  );
}

export function getDVACard(
  cards: IGetRecordResponse<CorePracticePatientInsuranceDetail>[]
): IDVACard | undefined {
  const dvaCard = getLatestWithMemberCode(cards.filter(isDVAInsurance));
  if (!dvaCard?.data.memberCode) {
    return;
  }
  return {
    number: dvaCard.data.memberCode,
    expiryDate: getExpiryDate(dvaCard.data.endDate),
  };
}

function getExpiryDate(endDate?: string): Timestamp | undefined {
  if (!endDate) {
    return;
  }
  const expiryDate = moment.utc(endDate, ISO_DATE_TIME_FORMAT);
  return expiryDate.isValid() && expiryDate.year() < NO_EXPIRY_YEAR
    ? toTimestamp(expiryDate)
    : undefined;
}

export function getHealthcareCard(
  cards: IGetRecordResponse<CorePracticePatientInsuranceDetail>[]
): IHealthFundCard | undefined {
  const healthFundCard = getLatestWithMemberCode(
    cards.filter(
      (insuranceDetail) =>
        !isDVAInsurance(insuranceDetail) &&
        !MedicareNumber.isValid(insuranceDetail.data.memberCode)
    )
  );
  if (!healthFundCard) {
    return;
  }
  return {
    fundCode: healthFundCard.data.insuranceName,
    membershipNumber: healthFundCard.data.memberCode ?? '',
    memberNumber: healthFundCard.data.series ?? '',
  };
}

export async function getPreferredFeeSchedule(
  insuranceDetails: IGetRecordResponse<CorePracticePatientInsuranceDetail>[],
  insuranceFeeSchedules: WithPrimaryKey<ITranslationMap<IFeeSchedule>>[]
): Promise<INamedDocument<IFeeSchedule> | undefined> {
  const feeScheduleRef = first(
    compact(
      sortBy(insuranceDetails, (insuranceDetail) => insuranceDetail.data.id)
        .reverse()
        .map(
          (insuranceDetail) =>
            insuranceFeeSchedules.find(
              (mapping) =>
                mapping.sourceIdentifier ===
                insuranceDetail.data.insuranceId.toString()
            )?.destinationIdentifier
        )
    )
  );
  if (!feeScheduleRef) {
    return;
  }
  return toNamedDocument(await Firestore.getDoc(feeScheduleRef));
}
